// Copyright (c) 2024, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { createApi, fetchBaseQuery, retry } from "@reduxjs/toolkit/query/react";
import {
  Connector,
  ConnectorMeta,
  ConnectorName,
  Release,
} from "../types/types";

const CONNECTORS = "/connectors";

export const connectorStoreApi = createApi({
  reducerPath: "connectorApi",
  baseQuery: retry(
    fetchBaseQuery({
      baseUrl: window.config.BACKEND_BASE_URL,
    }),
    { maxRetries: 3 }
  ),
  tagTypes: ["Connector"],
  endpoints: (builder) => ({
    searchConnectors: builder.query<
      Connector[],
      {
        limit?: number;
        offset?: number;
        connectorName?: string;
        description?: string;
        product?: string;
        version?: string;
        category?: string;
        label?: string;
      }
    >({
      query: ({
        limit,
        offset,
        connectorName,
        description,
        product,
        version,
        category,
        label,
      }) => ({
        url: `${CONNECTORS}/search`,
        method: "POST",
        body: {
          limit,
          offset,
          connectorName,
          description,
          version,
          product,
          category,
          label,
        },
      }),
    }),
    getConnectorById: builder.query<Connector, string>({
      query: (connectorId) => `${CONNECTORS}/${connectorId}`,
    }),
    getConnectorMeta: builder.query<
      ConnectorMeta,
      {
        connectorName?: string;
        description?: string;
        product?: string;
        version?: string;
        category?: string;
        label?: string;
      }
    >({
      query: ({ connectorName, description, product, version, category, label }) => {
        let queryString = `${CONNECTORS}/meta`;
        const queryParams = new URLSearchParams();

        if (connectorName) queryParams.append("connectorName", connectorName);
        if (description) queryParams.append("description", description);
        if (product) queryParams.append("product", product);
        if (version) queryParams.append("version", version);
        if (category) queryParams.append("category", category);
        if (label) queryParams.append("label", label);

        if (queryParams.toString() !== "") {
          queryString += `?${queryParams.toString()}`;
        }
        return queryString;
      },
    }),
    getConnectorNames: builder.query<ConnectorName[], void>({
      query: () => `${CONNECTORS}/names`,
    }),
    getConnectorLogo: builder.query<{ blobContent: any }, string>({
      query: (connectorId) => `${CONNECTORS}/${connectorId}/logo`,
      extraOptions: { maxRetries: 0 },
    }),
    getConnectorCategories: builder.query<string[], string>({
      query: (product) => `${CONNECTORS}/categories?product=${product}`,
    }),
    getConnectorLabels: builder.query<string[], string>({
      query: (product) => `${CONNECTORS}/labels?product=${product}`,
    }),
    getConnectorProducts: builder.query<string[], void>({
      query: () => `${CONNECTORS}/products`,
    }),
    getConnectorVersions: builder.query<string[], string>({
      query: (productId) => `${CONNECTORS}/products/${productId}/versions`,
    }),
    getReleasesByConnectorId: builder.query<Release[], string>({
      query: (connectorId) => `${CONNECTORS}/${connectorId}/releases`,
    }),
    downloadConnector: builder.mutation({
      query: (assetId) => ({
        url: `assets/${assetId}/download`,
        method: "POST",
      }),
    }),
  }),
});

export const {
  useSearchConnectorsQuery,
  useGetConnectorMetaQuery,
  useGetConnectorByIdQuery,
  useGetConnectorNamesQuery,
  useGetConnectorLogoQuery,
  useGetConnectorCategoriesQuery,
  useGetConnectorLabelsQuery,
  useGetConnectorProductsQuery,
  useGetConnectorVersionsQuery,
  useGetReleasesByConnectorIdQuery,
  useDownloadConnectorMutation
} = connectorStoreApi;
